import { useEffect, useState } from "react";
import api from "../constant/api";
import { motion, AnimatePresence } from "framer-motion";
import { pdf } from "@react-pdf/renderer";
import QuotationPDF from "./QuotationPDF";

export default function QuotationList() {

  // =====================================================
  // LIST STATES
  // =====================================================

  const [quotations, setQuotations] = useState([]);

  const [search, setSearch] = useState("");
  const [tempSearch, setTempSearch] = useState("");

  const [currentPage, setCurrentPage] = useState(1);

  const [showFilter, setShowFilter] = useState(false);

  // =====================================================
  // FILTER
  // =====================================================

  const [statusFilter, setStatusFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("all");

  const [tempStatus, setTempStatus] = useState("");
  const [tempDateFilter, setTempDateFilter] = useState("all");

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [tempFromDate, setTempFromDate] = useState("");
  const [tempToDate, setTempToDate] = useState("");

  // =====================================================
  // FULL QUOTATION
  // =====================================================

  const [fullQuotation, setFullQuotation] = useState(null);

  const [quotationDiscount, setQuotationDiscount] = useState(0);
  const [quotationGstPercent, setQuotationGstPercent] =
    useState(0);

  // =====================================================
  // USER
  // =====================================================

  const user = JSON.parse(
    localStorage.getItem("user") || "{}"
  );

  const user_id = user.user_id;

  const [showProductModal, setShowProductModal] =
  useState(false);

const [productSearch, setProductSearch] =
  useState("");

const [products, setProducts] =
  useState([]);

const [filteredProducts, setFilteredProducts] =
  useState([]);

const [productLoading, setProductLoading] =
  useState(false);

const [addingProductId, setAddingProductId] =
  useState(null);


  const addQuotationItem = async () => {
  if (!fullQuotation?.quotation?.quotation_id) {
    alert("Quotation ID missing");
    return;
  }

  setProductSearch("");
  setShowProductModal(true);

  await loadProducts("");
};

const loadProducts = async (search = "") => {
  try {
    setProductLoading(true);

    const res = await api.get(
      "/Product/getProduct"
    );

    const list =
      res.data?.data ||
      res.data?.products ||
      res.data ||
      [];

    const safeProducts =
      Array.isArray(list)
        ? list
        : [];

    setProducts(safeProducts);

    const keyword =
      String(search || "")
        .toLowerCase()
        .trim();

    if (!keyword) {
      setFilteredProducts(
        safeProducts.slice(0, 50)
      );
      return;
    }

    const filtered =
      safeProducts.filter((product) => {
        const name =
          String(
            product.product_name || ""
          ).toLowerCase();

        const code =
          String(
            product.product_code || ""
          ).toLowerCase();

        return (
          name.includes(keyword) ||
          code.includes(keyword)
        );
      });

    setFilteredProducts(
      filtered.slice(0, 50)
    );

  } catch (error) {
    console.error(
      "PRODUCT LOAD ERROR:",
      error
    );

    alert(
      error?.response?.data?.message ||
      "Failed to load products"
    );

  } finally {
    setProductLoading(false);
  }
};

const handleProductSearch = (value) => {
  setProductSearch(value);

  const keyword =
    String(value || "")
      .toLowerCase()
      .trim();

  if (!keyword) {
    setFilteredProducts(
      products.slice(0, 50)
    );
    return;
  }

  const filtered =
    products.filter((product) => {

      const name =
        String(
          product.product_name || ""
        ).toLowerCase();

      const code =
        String(
          product.product_code || ""
        ).toLowerCase();

      const id =
        String(
          product.product_id || ""
        ).toLowerCase();

      return (
        name.includes(keyword) ||
        code.includes(keyword) ||
        id.includes(keyword)
      );
    });

  setFilteredProducts(
    filtered.slice(0, 50)
  );
};

const selectProductForQuotation = async (
  product
) => {

  const quotation_id =
    fullQuotation?.quotation?.quotation_id;

  if (!quotation_id) {
    alert("Quotation ID missing");
    return;
  }

  if (!product?.product_id) {
    alert("Product ID missing");
    return;
  }

  try {

    setAddingProductId(
      product.product_id
    );

    const price =
      Number(product.price || 0);

    const res = await api.post(
      "/Ecom/addQuotationItem",
      {
        quotation_id:

          Number(quotation_id),

        product_id:
          Number(product.product_id),

        qty: 1,

        pic: 0,

        price,

        sqft: 0,

        total: 0,
      }
    );

    console.log(
      "ADD ITEM RESPONSE:",
      res.data
    );

    if (!res.data?.success) {
      throw new Error(
        res.data?.message ||
        "Quotation item insert failed"
      );
    }

    const quotation_item_id =
      res.data?.quotation_item_id ||
      res.data?.data?.quotation_item_id;

    if (!quotation_item_id) {
      throw new Error(
        "quotation_item_id not returned from server"
      );
    }

    // Add directly into current UI
    const newItem = {
      quotation_item_id:

        Number(quotation_item_id),

      quotation_id:

        Number(quotation_id),

      product_id:

        Number(product.product_id),

      product_name:
        product.product_name || "",

      product_code:
        product.product_code || "",

      qty: 1,

      pic: 0,

      price,

      sqft: 0,

      total: 0,
    };

    setFullQuotation((prev) => {

      if (!prev) return prev;

      return {
        ...prev,

        items: [
          ...(prev.items || []),
          newItem,
        ],
      };
    });

    // Close popup
    setShowProductModal(false);

    setProductSearch("");

  } catch (error) {

    console.error(
      "SELECT PRODUCT ERROR:",
      error
    );

    alert(
      error?.response?.data?.message ||
      error?.message ||
      "Failed to add quotation item"
    );

  } finally {

    setAddingProductId(null);
  }
};

  // =====================================================
  // PAGINATION
  // =====================================================

  const itemsPerPage = 5;

  // =====================================================
  // DATE NORMALIZE
  // =====================================================

  const normalizeDate = (date) => {

    if (!date) return null;

    const d = new Date(date);

    if (isNaN(d.getTime())) return null;

    d.setHours(0, 0, 0, 0);

    return d;
  };

  // =====================================================
  // GET QUOTATIONS
  // =====================================================

  const getQuotation = async () => {

    try {

      const res = await api.get(
        "/Ecom/getQuotation"
      );

      console.log(
        "QUOTATIONS:",
        res.data
      );

      setQuotations(
        res.data?.data ||
        res.data ||
        []
      );

    } catch (error) {

      console.error(
        "GET QUOTATION ERROR:",
        error
      );

    }
  };

  // =====================================================
  // INITIAL LOAD
  // =====================================================

  useEffect(() => {

    getQuotation();

  }, []);

  // =====================================================
  // OPEN QUOTATION
  // =====================================================

  const openQuotation = async (
    quotation_id,
    qData
  ) => {

    if (!quotation_id) return;

    try {

      const res = await api.get(
        `/Ecom/getQuotationItems/${quotation_id}`
      );

      console.log(
        "GET QUOTATION ITEMS RESPONSE:",
        res.data
      );

      const items =
        res.data?.items ||
        res.data?.data ||
        res.data?.data?.items ||
        [];

      const quotation =
        res.data?.quotation ||
        res.data?.data?.quotation ||
        qData ||
        {};

      setQuotationDiscount(
        Number(
          quotation?.discount ||
          qData?.discount ||
          0
        )
      );

      setQuotationGstPercent(
        Number(
          quotation?.gst_percent ||
          qData?.gst_percent ||
          0
        )
      );

      setFullQuotation({

        quotation: {
          ...qData,
          ...quotation,

          quotation_id,

          discount: Number(
            quotation?.discount ||
            qData?.discount ||
            0
          ),

          gst_percent: Number(
            quotation?.gst_percent ||
            qData?.gst_percent ||
            0
          ),

          customer_gst_amount: Number(
            quotation?.customer_gst_amount ||
            qData?.customer_gst_amount ||
            0
          ),

          net_total: Number(
            quotation?.net_total ||
            qData?.net_total ||
            0
          )
        },

        items: items.map(item => ({

          ...item,

          // IMPORTANT
          quotation_items_id:
            item.quotation_items_id ??
            item.id ??
            null,

          qty: Number(item.qty || 0),

          pic: Number(item.pic || 0),

          price: Number(item.price || 0),

          sqft: Number(item.sqft || 0),

          total: Number(item.total || 0),

          sqft_per_box:
            Number(
              item.sqft_per_box || 0
            ),

          sqft_per_pcs:
            Number(
              item.sqft_per_pcs || 0
            )

        }))

      });

    } catch (error) {

      console.error(
        "OPEN QUOTATION ERROR:",
        error
      );

      alert(
        error?.response?.data?.message ||
        "Quotation loading failed"
      );

    }

  };

  // =====================================================
  // FILTERED DATA
  // =====================================================

  const filtered = quotations.filter(q => {

    const customerName =
      String(
        q?.customer_name || ""
      ).toLowerCase();

    const phone =
      String(
        q?.phone || ""
      );

    const searchValue =
      search.toLowerCase();

    const matchesSearch =
      customerName.includes(
        searchValue
      ) ||
      phone.includes(search);

    const matchesStatus =
      statusFilter === "" ||
      q?.status === statusFilter;

    let matchesDate = true;

    const qDate =
      normalizeDate(
        q?.created_at
      );

    const today =
      normalizeDate(
        new Date()
      );

    if (
      dateFilter === "today" &&
      qDate &&
      today
    ) {

      matchesDate =
        qDate.getTime() ===
        today.getTime();

    }

    if (
      dateFilter === "month" &&
      qDate &&
      today
    ) {

      matchesDate =
        qDate.getMonth() ===
          today.getMonth() &&
        qDate.getFullYear() ===
          today.getFullYear();

    }

    // CUSTOM RANGE

    if (fromDate || toDate) {

      const from =
        fromDate
          ? normalizeDate(fromDate)
          : null;

      const to =
        toDate
          ? normalizeDate(toDate)
          : null;

      if (
        from &&
        qDate &&
        qDate < from
      ) {
        return false;
      }

      if (
        to &&
        qDate &&
        qDate > to
      ) {
        return false;
      }

      matchesDate = true;
    }

    return (
      matchesSearch &&
      matchesStatus &&
      matchesDate
    );

  });

  // =====================================================
  // PAGINATION
  // =====================================================

  const indexOfLast =
    currentPage * itemsPerPage;

  const indexOfFirst =
    indexOfLast - itemsPerPage;

  const currentData =
    filtered.slice(
      indexOfFirst,
      indexOfLast
    );

  const totalPages =
    Math.ceil(
      filtered.length /
      itemsPerPage
    );

  // =====================================================
  // STATISTICS
  // =====================================================

  const totalQuotations =
    quotations.length;

  const totalSales =
    quotations.reduce(
      (sum, q) =>
        sum +
        Number(
          q?.net_total ||
          q?.total ||
          0
        ),
      0
    );

  const completedCount =
    quotations.filter(
      q =>
        q?.status ===
        "completed"
    ).length;

  const pendingCount =
    quotations.filter(
      q =>
        q?.status ===
        "pending"
    ).length;

  // =====================================================
  // UPDATE ITEM LOCALLY
  // =====================================================

  
  const updateQuotationItem = (
    index,
    field,
    value
  ) => {

    if (!fullQuotation) return;

    const updatedItems =
      [...fullQuotation.items];

    const item =
      {
        ...updatedItems[index],
        [field]: value
      };

    const box =
      Number(item.qty || 0);

    const pic =
      Number(item.pic || 0);

    const sqftPerBox =
      Number(
        item.sqft_per_box || 0
      );

    const sqftPerPcs =
      Number(
        item.sqft_per_pcs || 0
      );

    const price =
      Number(item.price || 0);

    const sqft =
      (
        box * sqftPerBox
      ) +
      (
        pic * sqftPerPcs
      );

    const total =
      sqft * price;

    item.sqft = sqft;
    item.total = total;

    updatedItems[index] =
      item;

    setFullQuotation(prev => ({
      ...prev,
      items: updatedItems
    }));

  };

  // =====================================================
  // ADD QUOTATION ITEM
  // =====================================================

  // const addQuotationItem =
  //   async () => {

  //     try {

  //       const quotation_id =
  //         fullQuotation
  //           ?.quotation
  //           ?.quotation_id;

  //       if (!quotation_id) {

  //         alert(
  //           "Quotation ID not found"
  //         );

  //         return;
  //       }

  //       // GET PRODUCTS

  //       const res =
  //         await api.get(
  //           "/Product/getProduct"
  //         );

  //       const products =
  //         res.data?.data ||
  //         res.data ||
  //         [];

  //       if (!products.length) {

  //         alert(
  //           "No products found"
  //         );

  //         return;
  //       }

  //       // For now first product.
  //       // You can later replace this
  //       // with product selection popup.

  //       const product =
  //         products[0];

  //       const qty = 1;

  //       const pic = 0;

  //       const price =
  //         Number(
  //           product.price || 0
  //         );

  //       const sqftPerBox =
  //         Number(
  //           product.sqft_per_box || 0
  //         );

  //       const sqftPerPcs =
  //         Number(
  //           product.sqft_per_pcs || 0
  //         );

  //       const sqft =
  //         (
  //           qty *
  //           sqftPerBox
  //         ) +
  //         (
  //           pic *
  //           sqftPerPcs
  //         );

  //       const total =
  //         sqft * price;

  //       // ========================================
  //       // INSERT DATABASE
  //       // ========================================

  //       const insertRes =
  //         await api.post(
  //           "/Ecom/addQuotationItem",
  //           {

  //             quotation_id,

  //             product_id:
  //               product.product_id,

  //             qty,

  //             pic,

  //             price,

  //             sqft,

  //             total

  //           }
  //         );

  //       console.log(
  //         "ADD ITEM RESPONSE:",
  //         insertRes.data
  //       );

  //       const insertedItem =
  //         insertRes.data?.item;

  //       const quotation_items_id =
  //         insertRes.data
  //           ?.quotation_items_id ||
  //         insertedItem
  //           ?.quotation_items_id;

  //       if (!quotation_items_id) {

  //         alert(
  //           "quotation_items_id not returned from server"
  //         );

  //         return;
  //       }

  //       // ========================================
  //       // ADD TO UI
  //       // ========================================

  //       const newItem = {

  //         ...(insertedItem || {}),

  //         quotation_items_id,

  //         quotation_id,

  //         product_id:
  //           product.product_id,

  //         product_name:
  //           product.product_name,

  //         qty,

  //         pic,

  //         price,

  //         sqft,

  //         total,

  //         sqft_per_box:
  //           sqftPerBox,

  //         sqft_per_pcs:
  //           sqftPerPcs

  //       };

  //       setFullQuotation(prev => ({

  //         ...prev,

  //         items: [
  //           ...(prev.items || []),
  //           newItem
  //         ]

  //       }));

  //       alert(
  //         "Quotation item added successfully ✅"
  //       );

  //     } catch (error) {

  //       console.error(
  //         "ADD QUOTATION ITEM ERROR:",
  //         error
  //       );

  //       alert(
  //         error?.response?.data?.message ||
  //         "Failed to add quotation item"
  //       );

  //     }

  //   };

  // =====================================================
  // DELETE QUOTATION ITEM
  // =====================================================

  const removeQuotationItem =
    async (item) => {

      try {

        const quotation_items_id =
          item?.quotation_items_id;

        console.log(
          "REMOVE ITEM:",
          item
        );

        console.log(
          "QUOTATION ITEM ID:",
          quotation_items_id
        );

        if (!quotation_items_id) {

          alert(
            "Quotation item ID not found"
          );

          return;
        }

        const confirmDelete =
          window.confirm(
            "Remove this quotation item?"
          );

        if (!confirmDelete) {
          return;
        }

        // ========================================
        // DELETE DATABASE
        // ========================================

        await api.delete(
          `/Ecom/deleteQuotationItemEdit/${quotation_items_id}`
        );

        // ========================================
        // REMOVE UI
        // ========================================

        setFullQuotation(prev => ({

          ...prev,

          items:
            prev.items.filter(
              currentItem =>
                Number(
                  currentItem.quotation_items_id
                ) !==
                Number(
                  quotation_items_id
                )
            )

        }));

        alert(
          "Quotation item removed successfully ✅"
        );

      } catch (error) {

        console.error(
          "DELETE QUOTATION ITEM ERROR:",
          error
        );

        alert(
          error?.response?.data?.message ||
          "Failed to remove quotation item"
        );

      }

    };

  // =====================================================
  // CALCULATE QUOTATION
  // =====================================================

  const calculateQuotation =
    () => {

      const subtotal =
        fullQuotation?.items?.reduce(
          (sum, item) =>
            sum +
            Number(
              item?.total || 0
            ),
          0
        ) || 0;

      const discount =
        Number(
          quotationDiscount || 0
        );

      const taxableAmount =
        Math.max(
          subtotal -
          discount,
          0
        );

      const gstPercent =
        Number(
          quotationGstPercent || 0
        );

      const gstAmount =
        taxableAmount *
        gstPercent /
        100;

      const netTotal =
        taxableAmount +
        gstAmount;

      return {
        subtotal,
        discount,
        taxableAmount,
        gstPercent,
        gstAmount,
        netTotal
      };

    };

  // =====================================================
  // SAVE QUOTATION
  // =====================================================

  const saveQuotation =
    async () => {

      try {

        if (!fullQuotation) {
          return;
        }

        const quotation_id =
          fullQuotation
            ?.quotation
            ?.quotation_id;

        if (!quotation_id) {

          alert(
            "Quotation ID not found"
          );

          return;
        }

        const {
          subtotal,
          discount,
          gstPercent,
          gstAmount,
          netTotal
        } =
          calculateQuotation();

        // ==========================================
        // SAVE EACH ITEM
        // ==========================================

        for (
          const item
          of fullQuotation.items
        ) {

          const quotation_items_id =
            item?.quotation_items_id;

          if (!quotation_items_id) {

            console.error(
              "Missing quotation_items_id:",
              item
            );

            continue;
          }

          await api.post(
            "/Ecom/updateQuotationItem",
            {

              quotation_items_id,

              quotation_id,

              product_id:
                item.product_id,

              qty:
                Number(
                  item.qty || 0
                ),

              pic:
                Number(
                  item.pic || 0
                ),

              price:
                Number(
                  item.price || 0
                ),

              sqft:
                Number(
                  item.sqft || 0
                ),

              total:
                Number(
                  item.total || 0
                )

            }
          );

        }

        // ==========================================
        // SAVE TOTALS
        // ==========================================

        await api.post(
          "/Ecom/updateQuotationTotals",
          {

            quotation_id,

            total:
              subtotal,

            discount,

            gst_percent:
              gstPercent,

            gst_amount:
              gstAmount,

            net_total:
              netTotal

          }
        );

        // ==========================================
        // UPDATE LOCAL
        // ==========================================

        setFullQuotation(prev => ({

          ...prev,

          quotation: {

            ...prev.quotation,

            total:
              subtotal,

            discount,

            gst_percent:
              gstPercent,

            gst_amount:
              gstAmount,

            customer_gst_amount:
              gstAmount,

            net_total:
              netTotal

          }

        }));

        setQuotationDiscount(
          discount
        );

        setQuotationGstPercent(
          gstPercent
        );

        await getQuotation();

        alert(
          "Quotation updated successfully ✅"
        );

      } catch (error) {

        console.error(
          "SAVE QUOTATION ERROR:",
          error
        );

        alert(
          error?.response?.data?.message ||
          "Quotation update failed"
        );

      }

    };

  // =====================================================
  // PRINT PDF
  // =====================================================

  const handlePrint =
    async (quotation_id) => {

      try {

        const res =
          await api.get(
            `/Ecom/getQuotationItems/${quotation_id}`
          );

        const quotation =
          res.data?.quotation ||
          res.data?.data?.quotation;

        const items =
          res.data?.items ||
          res.data?.data ||
          [];

        const blob =
          await pdf(
            <QuotationPDF
              quotation={quotation}
              items={items}
            />
          ).toBlob();

        const url =
          URL.createObjectURL(
            blob
          );

        window.open(url);

      } catch (error) {

        console.error(
          "PDF ERROR:",
          error
        );

      }

    };

  // =====================================================
  // WHATSAPP
  // =====================================================

  const handleWhatsApp =
    () => {

      if (!fullQuotation) {
        return;
      }

      const q =
        fullQuotation.quotation;

      let message =
        `Quotation #${q.quotation_id}\n`;

      message +=
        `Name: ${q.customer_name}\n`;

      message +=
        `Phone: ${q.phone}\n`;

      message +=
        `Total: ₹${q.net_total || q.total}\n\n`;

      message +=
        `Items:\n`;

      fullQuotation.items.forEach(
        item => {

          message +=
            `• ${item.product_name || item.product_id}`;

          message +=
            ` - Qty: ${item.qty}`;

          message +=
            ` - ₹${item.total}\n`;

        }
      );

      const phone =
        String(
          q.phone || ""
        ).replace(
          /\D/g,
          ""
        );

      const url =
        `https://wa.me/${phone}?text=${encodeURIComponent(
          message
        )}`;

      window.open(
        url,
        "_blank"
      );

    };

  // =====================================================
  // CONVERT TO ORDER
  // =====================================================

  const convertToOrder =
    async (quotation) => {

      try {

        if (
          quotation.status ===
          "converted"
        ) {

          alert(
            "Already converted"
          );

          return;
        }

        const res =
          await api.get(
            `/Ecom/getQuotationItems/${quotation.quotation_id}`
          );

        const items =
          res.data?.items ||
          res.data?.data ||
          [];

        const orderRes =
          await api.post(
            "/Ecom/createOrderFromQuotation",
            {

              name:
                quotation.customer_name,

              phone:
                quotation.phone,

              gst_no:
                quotation.gst_no,

              email:
                quotation.email || "",

              city:
                quotation.city || "",

              address:
                quotation.address || "",

              payment_method:
                "Cash",

              total_amount:
                quotation.total,

              discount:
                quotation.discount,

              customer_gst_amount:
                quotation.customer_gst_amount,

              net_total:
                quotation.net_total,

              branch:
                quotation.branch,

              salesman_id:
                user_id

            }
          );

        const order_id =
          orderRes.data.order_id;

        for (
          const item
          of items
        ) {

          await api.post(
            "/Ecom/addOrderItems",
            {

              order_id,

              product_id:
                item.product_id,

              cart_id:
                null,

              price:
                item.price,

              pcs:
                item.pic,

              length:
                item.length || 0,

              width:
                item.width || 0,

              height:
                item.height || 0,

              qty:
                item.qty || 0,

              total_area:
                item.sqft || 0,

              item_amount:
                item.total || 0

            }
          );

        }

        await api.post(
          `/Ecom/updateQuotationStatus/${quotation.quotation_id}`,
          {
            status:
              "converted"
          }
        );

        await getQuotation();

        alert(
          "Order created successfully ✅"
        );

      } catch (error) {

        console.error(
          "CONVERT ERROR:",
          error
        );

        alert(
          "Conversion failed ❌"
        );

      }

    };

  // =====================================================
  // PAGINATION
  // =====================================================

  const getPagination =
    (
      currentPage,
      totalPages
    ) => {

      const pages = [];

      if (totalPages <= 7) {

        for (
          let i = 1;
          i <= totalPages;
          i++
        ) {
          pages.push(i);
        }

      } else {

        pages.push(1);

        if (
          currentPage > 3
        ) {
          pages.push("...");
        }

        const start =
          Math.max(
            2,
            currentPage - 1
          );

        const end =
          Math.min(
            totalPages - 1,
            currentPage + 1
          );

        for (
          let i = start;
          i <= end;
          i++
        ) {
          pages.push(i);
        }

        if (
          currentPage <
          totalPages - 2
        ) {
          pages.push("...");
        }

        pages.push(
          totalPages
        );

      }

      return pages;

    };

  // =====================================================
  // CONVERTED
  // =====================================================

  const isConverted =
    fullQuotation
      ?.quotation
      ?.status ===
    "converted";

  // =====================================================
  // RETURN
  // =====================================================

  return (

    <div className="w-full min-w-0 p-3 md:p-6">

      {/* =================================================
          HEADER
      ================================================= */}

      <div className="
        flex
        items-center
        justify-between
        gap-3
        mb-5
      ">

        <h1 className="
          text-xl
          md:text-2xl
          font-bold
          text-gray-900
        ">
          Quotation List
        </h1>

        <button
          onClick={() =>
            setShowFilter(true)
          }
          className="
            md:hidden
            bg-gray-900
            text-white
            px-4
            py-2
            rounded-xl
            shadow
            text-sm
          "
        >
          Filter ⚙️
        </button>

      </div>

      {/* =================================================
          DESKTOP FILTER
      ================================================= */}

      <div className="
        hidden
        md:flex
        items-center
        justify-between
        gap-4
        mb-6
        p-4
        rounded-2xl
        bg-white
        border
        shadow
      ">

        <div className="
          flex
          items-center
          gap-3
          flex-wrap
        ">

          {/* SEARCH */}

          <div className="relative">

            <input
              type="text"
              placeholder="Search customer / phone..."
              value={search}
              onChange={e => {

                setSearch(
                  e.target.value
                );

                setCurrentPage(1);

              }}
              className="
                pl-10
                pr-4
                py-2
                w-64
                rounded-xl
                border
                outline-none
              "
            />

            <span className="
              absolute
              left-3
              top-2.5
              text-gray-400
            ">
              🔍
            </span>

          </div>

          {/* DATE */}

          <div className="
            flex
            items-center
            gap-2
            bg-gray-100
            px-3
            py-2
            rounded-xl
          ">

            <input
              type="date"
              value={fromDate}
              onChange={e => {

                setFromDate(
                  e.target.value
                );

                setDateFilter(
                  "all"
                );

                setCurrentPage(1);

              }}
              className="
                bg-transparent
                outline-none
                text-sm
              "
            />

            <span>
              →
            </span>

            <input
              type="date"
              value={toDate}
              onChange={e => {

                setToDate(
                  e.target.value
                );

                setDateFilter(
                  "all"
                );

                setCurrentPage(1);

              }}
              className="
                bg-transparent
                outline-none
                text-sm
              "
            />

          </div>

        </div>

        {/* STATUS */}

        <div className="
          flex
          gap-2
        ">

          <button
            onClick={() =>
              setStatusFilter("")
            }
            className={`
              px-4
              py-2
              rounded-full
              text-sm
              ${
                statusFilter === ""
                  ? "bg-gray-900 text-white"
                  : "bg-gray-100"
              }
            `}
          >
            All
          </button>

          <button
            onClick={() =>
              setStatusFilter(
                "completed"
              )
            }
            className={`
              px-4
              py-2
              rounded-full
              text-sm
              ${
                statusFilter === "completed"
                  ? "bg-green-500 text-white"
                  : "bg-gray-100"
              }
            `}
          >
            ✔ Completed
          </button>

          <button
            onClick={() =>
              setStatusFilter(
                "pending"
              )
            }
            className={`
              px-4
              py-2
              rounded-full
              text-sm
              ${
                statusFilter === "pending"
                  ? "bg-yellow-500 text-white"
                  : "bg-gray-100"
              }
            `}
          >
            ⏳ Pending
          </button>

        </div>

      </div>

      {/* =================================================
          DESKTOP DASHBOARD
      ================================================= */}

      <div className="
        hidden
        md:grid
        grid-cols-4
        gap-4
        mb-6
      ">

        <div className="
          bg-gradient-to-r
          from-blue-600
          to-blue-400
          text-white
          p-5
          rounded-2xl
          shadow
        ">

          <p className="text-sm">
            Total Sales
          </p>

          <h2 className="text-2xl font-bold">
            ₹{totalSales.toLocaleString()}
          </h2>

        </div>

        <div className="
          bg-gradient-to-r
          from-purple-600
          to-purple-400
          text-white
          p-5
          rounded-2xl
          shadow
        ">

          <p className="text-sm">
            Total Quotations
          </p>

          <h2 className="text-2xl font-bold">
            {totalQuotations}
          </h2>

        </div>

        <div className="
          bg-gradient-to-r
          from-green-600
          to-green-400
          text-white
          p-5
          rounded-2xl
          shadow
        ">

          <p className="text-sm">
            Completed
          </p>

          <h2 className="text-2xl font-bold">
            {completedCount}
          </h2>

        </div>

        <div className="
          bg-gradient-to-r
          from-yellow-500
          to-yellow-300
          text-white
          p-5
          rounded-2xl
          shadow
        ">

          <p className="text-sm">
            Pending
          </p>

          <h2 className="text-2xl font-bold">
            {pendingCount}
          </h2>

        </div>

      </div>

      {/* =================================================
          DESKTOP TABLE
      ================================================= */}

      <div className="
        hidden
        md:block
        overflow-x-auto
        bg-white
        rounded-2xl
        shadow
        border
      ">

        <table className="
          w-full
          text-sm
        ">

          <thead className="
            bg-gray-900
            text-white
          ">

            <tr>

              <th className="p-4 text-left">
                #
              </th>

              <th className="p-4 text-left">
                Customer
              </th>

              <th className="p-4 text-left">
                Phone
              </th>

              <th className="p-4 text-left">
                Amount
              </th>

              <th className="p-4 text-left">
                Status
              </th>

              <th className="p-4 text-left">
                Date
              </th>

              <th className="p-4 text-left">
                Branch
              </th>

              <th className="p-4 text-center">
                Action
              </th>

            </tr>

          </thead>

          <tbody>

            {currentData.map(
              (q, i) => (

                <tr
                  key={
                    q.quotation_id ||
                    i
                  }
                  className="
                    border-b
                    hover:bg-gray-50
                  "
                >

                  <td className="p-4">
                    #{q.quotation_id}
                  </td>

                  <td className="p-4">

                    <div className="
                      flex
                      items-center
                      gap-3
                    ">

                      <div className="
                        w-10
                        h-10
                        rounded-full
                        bg-gradient-to-br
                        from-blue-500
                        to-purple-500
                        text-white
                        flex
                        items-center
                        justify-center
                        font-bold
                      ">
                        {q.customer_name
                          ?.charAt(0)}
                      </div>

                      <div>

                        <p className="
                          font-semibold
                        ">
                          {q.customer_name}
                        </p>

                        <p className="
                          text-xs
                          text-gray-400
                        ">
                          Customer
                        </p>

                      </div>

                    </div>

                  </td>

                  <td className="
                    p-4
                    text-gray-500
                  ">
                    {q.phone}
                  </td>

                  <td className="
                    p-4
                    font-bold
                    text-blue-600
                  ">
                    ₹
                    {Number(
                      q.net_total ||
                      q.total ||
                      0
                    ).toFixed(2)}
                  </td>

                  <td className="p-4">

                    <span className={`
                      px-3
                      py-1
                      rounded-full
                      text-xs
                      ${
                        q.status === "completed"
                          ? "bg-green-100 text-green-600"
                          : q.status === "pending"
                          ? "bg-yellow-100 text-yellow-600"
                          : q.status === "converted"
                          ? "bg-red-100 text-red-600"
                          : "bg-gray-100 text-gray-600"
                      }
                    `}>
                      {q.status || "pending"}
                    </span>

                  </td>

                  <td className="
                    p-4
                    text-gray-400
                  ">
                    {q.created_at?.slice(
                      0,
                      10
                    )}
                  </td>

                  <td className="
                    p-4
                    text-gray-400
                  ">
                    {q.branch}
                  </td>

                  <td className="p-4">

                    <div className="
                      flex
                      justify-center
                      gap-2
                    ">

                      <button
                        onClick={() =>
                          openQuotation(
                            q.quotation_id,
                            q
                          )
                        }
                        className="
                          bg-blue-500
                          text-white
                          px-3
                          py-1
                          rounded-lg
                        "
                      >
                        View
                      </button>

                      <button
                        onClick={() =>
                          handlePrint(
                            q.quotation_id
                          )
                        }
                        className="
                          bg-purple-500
                          text-white
                          px-3
                          py-1
                          rounded-lg
                        "
                      >
                        PDF
                      </button>

                      {q.status !== "converted" &&
                        q.status !== "pending" && (
                          <button
                            onClick={() =>
                              convertToOrder(q)
                            }
                            className="
                              bg-green-600
                              text-white
                              px-3
                              py-1
                              rounded-lg
                            "
                          >
                            Convert
                          </button>
                        )}

                    </div>

                  </td>

                </tr>

              )
            )}

          </tbody>

        </table>

      </div>

      {/* =================================================
          MOBILE
      ================================================= */}

      <div className="
        md:hidden
        space-y-4
      ">

        {/* MOBILE SUMMARY */}

        <div className="
          grid
          grid-cols-2
          gap-3
        ">

          <div className="
            bg-blue-600
            text-white
            p-4
            rounded-2xl
            shadow
          ">

            <p className="text-xs">
              Total Sales
            </p>

            <p className="text-lg font-bold">
              ₹
              {totalSales.toLocaleString()}
            </p>

          </div>

          <div
            onClick={() =>
              setStatusFilter("")
            }
            className="
              bg-gradient-to-r
              from-purple-600
              to-purple-400
              text-white
              p-4
              rounded-2xl
              shadow
              cursor-pointer
            "
          >

            <p className="text-xs">
              Quotations
            </p>

            <p className="text-lg font-bold">
              {totalQuotations}
            </p>

          </div>

          <div
            onClick={() =>
              setStatusFilter(
                "completed"
              )
            }
            className="
              bg-gradient-to-r
              from-green-600
              to-green-400
              text-white
              p-4
              rounded-2xl
              shadow
              cursor-pointer
            "
          >

            <p className="text-xs">
              Completed
            </p>

            <p className="text-lg font-bold">
              {completedCount}
            </p>

          </div>

          <div
            onClick={() =>
              setStatusFilter(
                "pending"
              )
            }
            className="
              bg-gradient-to-r
              from-yellow-500
              to-yellow-300
              text-white
              p-4
              rounded-2xl
              shadow
              cursor-pointer
            "
          >

            <p className="text-xs">
              Pending
            </p>

            <p className="text-lg font-bold">
              {pendingCount}
            </p>

          </div>

        </div>

        {/* MOBILE QUOTATION CARDS */}

        {currentData.map(
          (q, i) => (

            <div
              key={
                q.quotation_id ||
                i
              }
              className="
                bg-white
                border
                rounded-2xl
                shadow
                p-4
              "
            >

              <div className="
                flex
                items-center
                justify-between
              ">

                <div className="
                  flex
                  items-center
                  gap-3
                ">

                  <div className="
                    w-11
                    h-11
                    rounded-full
                    bg-gradient-to-br
                    from-blue-500
                    to-purple-500
                    text-white
                    flex
                    items-center
                    justify-center
                    font-bold
                  ">
                    {q.customer_name
                      ?.charAt(0) ||
                      "C"}
                  </div>

                  <div>

                    <h3 className="
                      font-semibold
                      text-gray-800
                    ">
                      {q.customer_name}
                    </h3>

                    <p className="
                      text-xs
                      text-gray-400
                    ">
                      ID: #{q.quotation_id}
                    </p>

                    <p className="
                      text-xs
                      text-gray-400
                    ">
                      Branch: {q.branch || "-"}
                    </p>

                  </div>

                </div>

                <span className={`
                  text-[10px]
                  px-2
                  py-1
                  rounded-full
                  font-semibold
                  ${
                    q.status === "completed"
                      ? "bg-green-100 text-green-600"
                      : q.status === "pending"
                      ? "bg-yellow-100 text-yellow-600"
                      : q.status === "converted"
                      ? "bg-red-100 text-red-600"
                      : "bg-gray-100 text-gray-600"
                  }
                `}>
                  {q.status || "pending"}
                </span>

              </div>

              <div className="
                mt-4
                grid
                grid-cols-2
                gap-3
              ">

                <div>

                  <p className="
                    text-xs
                    text-gray-400
                  ">
                    Phone
                  </p>

                  <p className="
                    text-sm
                    font-medium
                  ">
                    {q.phone || "-"}
                  </p>

                </div>

                <div className="
                  text-right
                ">

                  <p className="
                    text-xs
                    text-gray-400
                  ">
                    Amount
                  </p>

                  <p className="
                    text-lg
                    font-bold
                    text-blue-600
                  ">
                    ₹
                    {Number(
                      q.net_total ||
                      q.total ||
                      0
                    ).toFixed(2)}
                  </p>

                </div>

              </div>

              <div className="
                flex
                gap-2
                mt-4
              ">

                <button
                  onClick={() =>
                    openQuotation(
                      q.quotation_id,
                      q
                    )
                  }
                  className="
                    flex-1
                    bg-blue-600
                    text-white
                    py-2
                    rounded-xl
                    text-sm
                  "
                >
                  👁 View
                </button>

                <button
                  onClick={() =>
                    handlePrint(
                      q.quotation_id
                    )
                  }
                  className="
                    flex-1
                    bg-purple-600
                    text-white
                    py-2
                    rounded-xl
                    text-sm
                  "
                >
                  📄 PDF
                </button>
                 {/* <button
                  onClick={() =>
                    handlePrint(
                      q.quotation_id
                    )
                  }
                  className="
                    flex-1
                    bg-purple-600
                    text-white
                    py-2
                    rounded-xl
                    text-sm
                  "
                >
                  Convert
                </button> */}

                 {q.status !== "converted" &&
                        q.status !== "pending" && (
                          <button
                            onClick={() =>
                              convertToOrder(q)
                            }
                            className="
                              bg-green-600
                              text-white
                              px-3
                              py-1
                              rounded-lg
                            "
                          >
                            Convert
                          </button>
                        )}


              </div>

            </div>

          )
        )}

      </div>

      <AnimatePresence>

  {showProductModal && (

    <motion.div
      className="
        fixed
        inset-0
        z-[10000]
        bg-black/70
        backdrop-blur-sm
        flex
        items-center
        justify-center
        p-3
        md:p-6
      "
      initial={{
        opacity: 0
      }}
      animate={{
        opacity: 1
      }}
      exit={{
        opacity: 0
      }}
      onClick={() =>
        setShowProductModal(false)
      }
    >

      <motion.div
        className="
          bg-white
          w-full
          max-w-2xl
          max-h-[90vh]
          rounded-2xl
          shadow-2xl
          overflow-hidden
          flex
          flex-col
        "
        initial={{
          scale: 0.95,
          y: 20
        }}
        animate={{
          scale: 1,
          y: 0
        }}
        exit={{
          scale: 0.95,
          y: 20
        }}
        onClick={(e) =>
          e.stopPropagation()
        }
      >

        {/* HEADER */}

        <div
          className="
            bg-gradient-to-r
            from-gray-900
            to-gray-700
            text-white
            p-4
            flex
            items-center
            justify-between
          "
        >

          <div>

            <h2
              className="
                text-lg
                md:text-xl
                font-bold
              "
            >
              Select Product
            </h2>

            <p
              className="
                text-xs
                text-white/70
                mt-1
              "
            >
              Search and select a product
            </p>

          </div>

          <button
            type="button"
            onClick={() =>
              setShowProductModal(false)
            }
            className="
              text-white
              text-xl
              px-2
            "
          >
            ✕
          </button>

        </div>


        {/* SEARCH */}

        <div
          className="
            p-3
            md:p-4
            border-b
            bg-gray-50
          "
        >

          <div
            className="
              relative
            "
          >

            <input
              type="text"
              value={productSearch}
              onChange={(e) =>
                handleProductSearch(
                  e.target.value
                )
              }
              placeholder="
                Search product name / code / ID...
              "
              autoFocus
              className="
                w-full
                border
                border-gray-300
                rounded-xl
                px-4
                py-3
                text-sm
                outline-none
                focus:ring-2
                focus:ring-blue-500
              "
            />

          </div>

        </div>


        {/* PRODUCTS */}

        <div
          className="
            flex-1
            overflow-y-auto
            p-3
            md:p-4
          "
        >

          {productLoading ? (

            <div
              className="
                text-center
                py-10
                text-gray-500
              "
            >
              Loading products...
            </div>

          ) : filteredProducts.length === 0 ? (

            <div
              className="
                text-center
                py-10
                text-gray-400
              "
            >
              No products found
            </div>

          ) : (

            <div
              className="
                space-y-2
              "
            >

              {filteredProducts.map(
                (product) => {

                  const isAdding =
                    Number(
                      addingProductId
                    ) ===
                    Number(
                      product.product_id
                    );

                  return (

                    <button
                      key={
                        product.product_id
                      }
                      type="button"
                      disabled={isAdding}
                      onClick={() =>
                        selectProductForQuotation(
                          product
                        )
                      }
                      className="
                        w-full
                        text-left
                        border
                        border-gray-200
                        rounded-xl
                        p-3
                        hover:border-blue-500
                        hover:bg-blue-50
                        transition
                        active:scale-[0.99]
                        disabled:opacity-50
                      "
                    >

                      <div
                        className="
                          flex
                          items-center
                          justify-between
                          gap-3
                        "
                      >

                        <div
                          className="
                            min-w-0
                            flex-1
                          "
                        >

                          <div
                            className="
                              font-semibold
                              text-gray-800
                              text-sm
                              truncate
                            "
                          >
                            {
                              product.product_name ||
                              "Unnamed Product"
                            }
                          </div>

                          <div
                            className="
                              flex
                              flex-wrap
                              gap-2
                              mt-1
                              text-[11px]
                              text-gray-500
                            "
                          >

                            <span>
                              ID:{" "}
                              {
                                product.product_id
                              }
                            </span>

                            {product.product_code && (
                              <span>
                                Code:{" "}
                                {
                                  product.product_code
                                }
                              </span>
                            )}

                          </div>

                        </div>


                        <div
                          className="
                            text-right
                            shrink-0
                          "
                        >

                          <div
                            className="
                              font-bold
                              text-blue-600
                              text-sm
                            "
                          >
                            ₹
                            {Number(
                              product.price || 0
                            ).toFixed(2)}
                          </div>

                          <div
                            className="
                              text-[10px]
                              text-gray-400
                            "
                          >
                            {isAdding
                              ? "Adding..."
                              : "Tap to add"}
                          </div>

                        </div>

                      </div>

                    </button>

                  );

                }
              )}

            </div>

          )}

        </div>


        {/* FOOTER */}

        <div
          className="
            p-3
            border-t
            bg-gray-50
            flex
            justify-end
          "
        >

          <button
            type="button"
            onClick={() =>
              setShowProductModal(false)
            }
            className="
              px-4
              py-2
              rounded-lg
              bg-gray-200
              hover:bg-gray-300
              text-sm
            "
          >
            Cancel
          </button>

        </div>

      </motion.div>

    </motion.div>

  )}

</AnimatePresence>

      {/* =================================================
          QUOTATION MODAL
      ================================================= */}

      <AnimatePresence>

        {fullQuotation && (

          <motion.div
            className="
              fixed
              inset-0
              bg-black/70
              backdrop-blur-sm
              flex
              items-center
              justify-center
              z-[9999]
              p-0
              md:p-6
            "
            initial={{
              opacity: 0
            }}
            animate={{
              opacity: 1
            }}
            exit={{
              opacity: 0
            }}
            onClick={() =>
              setFullQuotation(null)
            }
          >

            <motion.div
              className="
                bg-white
                w-full
                h-full
                md:h-auto
                md:max-w-4xl
                md:max-h-[90vh]
                md:rounded-2xl
                shadow-2xl
                overflow-hidden
                flex
                flex-col
              "
              initial={{
                scale: 0.95,
                y: 30
              }}
              animate={{
                scale: 1,
                y: 0
              }}
              exit={{
                scale: 0.95,
                y: 30
              }}
              onClick={e =>
                e.stopPropagation()
              }
            >

              {/* HEADER */}

              <div className="
                bg-gradient-to-r
                from-gray-900
                to-gray-700
                text-white
                p-4
                md:p-5
                flex
                justify-between
                items-center
                shrink-0
              ">

                <div>

                  <h2 className="
                    text-lg
                    md:text-xl
                    font-bold
                  ">
                    Quotation #
                    {
                      fullQuotation
                        ?.quotation
                        ?.quotation_id
                    }
                  </h2>

                  <p className="
                    text-sm
                    opacity-80
                  ">
                    {
                      fullQuotation
                        ?.quotation
                        ?.customer_name
                    }
                  </p>

                </div>

                <button
                  onClick={() =>
                    setFullQuotation(null)
                  }
                  className="
                    text-white
                    text-xl
                  "
                >
                  ✖
                </button>

              </div>

              {/* BODY */}

              <div className="
                p-3
                md:p-5
                space-y-4
                overflow-y-auto
                flex-1
              ">

                {/* CUSTOMER */}

                <div className="
                  grid
                  grid-cols-2
                  md:grid-cols-3
                  gap-3
                  bg-gray-50
                  p-3
                  md:p-4
                  rounded-xl
                ">

                  <div>

                    <p className="
                      text-xs
                      text-gray-500
                    ">
                      Phone
                    </p>

                    <p className="
                      font-semibold
                      text-sm
                    ">
                      {
                        fullQuotation
                          ?.quotation
                          ?.phone ||
                        "-"
                      }
                    </p>

                  </div>

                  <div>

                    <p className="
                      text-xs
                      text-gray-500
                    ">
                      Address
                    </p>

                    <p className="
                      font-semibold
                      text-sm
                      break-words
                    ">
                      {
                        fullQuotation
                          ?.quotation
                          ?.address ||
                        "-"
                      }
                    </p>

                  </div>

                  <div>

                    <p className="
                      text-xs
                      text-gray-500
                    ">
                      Status
                    </p>

                    <span className="
                      inline-block
                      mt-1
                      px-2
                      py-1
                      rounded
                      text-xs
                      bg-gray-100
                    ">
                      {
                        fullQuotation
                          ?.quotation
                          ?.status ||
                        "pending"
                      }
                    </span>

                  </div>

                </div>

                {/* ACTION BAR */}

                <div className="
                  flex
                  flex-col
                  md:flex-row
                  md:items-center
                  md:justify-between
                  gap-3
                ">

                  <h3 className="
                    font-bold
                    text-lg
                  ">
                    Items
                  </h3>

                  <div className="
                    grid
                    grid-cols-2
                    md:flex
                    gap-2
                  ">

                    {!isConverted && (

                     <button
  onClick={addQuotationItem}
  className="
    bg-blue-600
    hover:bg-blue-700
    text-white
    px-3
    py-2
    rounded-lg
    text-sm
  "
>
  ➕ Add Item
</button>

                    )}

                    <button
                      onClick={
                        handleWhatsApp
                      }
                      className="
                        bg-green-500
                        hover:bg-green-600
                        text-white
                        px-3
                        py-2
                        rounded-lg
                        text-sm
                      "
                    >
                      📲 WhatsApp
                    </button>

                  </div>

                </div>

                {/* ITEMS */}

                <div className="
                  space-y-3
                ">

                  {fullQuotation?.items
                    ?.map(
                      (item, i) => (

                        <div
                          key={
                            item.quotation_items_id ||
                            `item-${i}`
                          }
                          className="
                            bg-white
                            border
                            rounded-xl
                            p-3
                            md:p-4
                            shadow-sm
                          "
                        >

                          {/* ITEM HEADER */}

                          <div className="
                            flex
                            justify-between
                            items-start
                            gap-2
                            mb-3
                          ">

                            <div>

                              <h3 className="
                                font-semibold
                                text-gray-800
                                text-sm
                                md:text-base
                              ">
                                {
                                  item.product_name ||
                                  `Product ${item.product_id}`
                                }
                              </h3>

                              <p className="
                                text-[11px]
                                text-gray-400
                              ">
                                Product ID:
                                {" "}
                                {item.product_id}
                              </p>

                              <p className="
                                text-[11px]
                                text-blue-500
                              ">
                                Quotation Item ID:
                                {" "}
                                {item.quotation_items_id || "-"}
                              </p>

                            </div>

                            {!isConverted && (

                              <button
                                onClick={() =>
                                  removeQuotationItem(
                                    item
                                  )
                                }
                                className="
                                  shrink-0
                                  text-red-500
                                  border
                                  border-red-200
                                  px-3
                                  py-1
                                  rounded-lg
                                  text-xs
                                  hover:bg-red-50
                                "
                              >
                                🗑 Remove
                              </button>

                            )}

                          </div>

                          {/* INPUT GRID */}

                          <div className="
                            grid
                            grid-cols-2
                            md:grid-cols-4
                            gap-3
                          ">

                            {/* BOX */}

                            <div>

                              <label className="
                                text-xs
                                text-gray-500
                              ">
                                Box
                              </label>

                              <input
                                type="number"
                                min="0"
                                value={
                                  item.qty ?? 0
                                }
                                disabled={
                                  isConverted
                                }
                                onChange={e =>
                                  updateQuotationItem(
                                    i,
                                    "qty",
                                    e.target.value
                                  )
                                }
                                className="
                                  w-full
                                  border
                                  rounded-lg
                                  p-2
                                  text-sm
                                  mt-1
                                "
                              />

                            </div>

                            {/* PCS */}

                            <div>

                              <label className="
                                text-xs
                                text-gray-500
                              ">
                                Pcs
                              </label>

                              <input
                                type="number"
                                min="0"
                                value={
                                  item.pic ?? 0
                                }
                                disabled={
                                  isConverted
                                }
                                onChange={e =>
                                  updateQuotationItem(
                                    i,
                                    "pic",
                                    e.target.value
                                  )
                                }
                                className="
                                  w-full
                                  border
                                  rounded-lg
                                  p-2
                                  text-sm
                                  mt-1
                                "
                              />

                            </div>

                            {/* RATE */}

                            <div>

                              <label className="
                                text-xs
                                text-gray-500
                              ">
                                Rate
                              </label>

                              <input
                                type="number"
                                min="0"
                                value={
                                  item.price ?? 0
                                }
                                disabled={
                                  isConverted
                                }
                                onChange={e =>
                                  updateQuotationItem(
                                    i,
                                    "price",
                                    e.target.value
                                  )
                                }
                                className="
                                  w-full
                                  border
                                  rounded-lg
                                  p-2
                                  text-sm
                                  mt-1
                                "
                              />

                            </div>

                            {/* SQFT */}

                            <div>

                              <label className="
                                text-xs
                                text-gray-500
                              ">
                                Sqft
                              </label>

                              <div className="
                                w-full
                                border
                                rounded-lg
                                p-2
                                bg-gray-50
                                text-sm
                                mt-1
                              ">
                                {Number(
                                  item.sqft || 0
                                ).toFixed(2)}
                              </div>

                            </div>

                          </div>

                          {/* ITEM TOTAL */}

                          <div className="
                            flex
                            justify-between
                            items-center
                            border-t
                            mt-3
                            pt-3
                          ">

                            <span className="
                              text-sm
                              text-gray-500
                            ">
                              Item Total
                            </span>

                            <span className="
                              font-bold
                              text-blue-600
                            ">
                              ₹
                              {Number(
                                item.total || 0
                              ).toFixed(2)}
                            </span>

                          </div>

                        </div>

                      )
                    )}

                  {!fullQuotation?.items?.length && (

                    <div className="
                      text-center
                      py-8
                      text-gray-400
                      border
                      rounded-xl
                    ">
                      No quotation items
                    </div>

                  )}

                </div>

                {/* CALCULATION */}

                {(() => {

                  const {
                    subtotal,
                    discount,
                    taxableAmount,
                    gstPercent,
                    gstAmount,
                    netTotal
                  } =
                    calculateQuotation();

                  return (

                    <div className="
                      border-t
                      pt-4
                    ">

                      <div className="
                        w-full
                        md:w-96
                        md:ml-auto
                        space-y-3
                      ">

                        {/* SUBTOTAL */}

                        <div className="
                          flex
                          justify-between
                        ">

                          <span className="
                            text-gray-500
                          ">
                            Subtotal
                          </span>

                          <span className="
                            font-semibold
                          ">
                            ₹
                            {subtotal.toFixed(2)}
                          </span>

                        </div>

                        {/* DISCOUNT */}

                        <div className="
                          flex
                          justify-between
                          items-center
                          gap-3
                        ">

                          <span className="
                            text-gray-500
                          ">
                            Discount
                          </span>

                          {!isConverted ? (

                            <input
                              type="number"
                              min="0"
                              value={
                                quotationDiscount
                              }
                              onChange={e =>
                                setQuotationDiscount(
                                  e.target.value
                                )
                              }
                              className="
                                w-28
                                border
                                rounded-lg
                                p-2
                                text-right
                              "
                            />

                          ) : (

                            <span>
                              ₹
                              {discount.toFixed(2)}
                            </span>

                          )}

                        </div>

                        {/* TAXABLE */}

                        <div className="
                          flex
                          justify-between
                        ">

                          <span className="
                            text-gray-500
                          ">
                            Taxable Amount
                          </span>

                          <span className="
                            font-semibold
                          ">
                            ₹
                            {taxableAmount.toFixed(2)}
                          </span>

                        </div>

                        {/* GST */}

                        <div className="
                          flex
                          justify-between
                          items-center
                        ">

                          <span className="
                            text-gray-500
                          ">
                            GST %
                          </span>

                          {!isConverted ? (

                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={
                                quotationGstPercent
                              }
                              onChange={e =>
                                setQuotationGstPercent(
                                  e.target.value
                                )
                              }
                              className="
                                w-28
                                border
                                rounded-lg
                                p-2
                                text-right
                              "
                            />

                          ) : (

                            <span>
                              {gstPercent}%
                            </span>

                          )}

                        </div>

                        {/* GST AMOUNT */}

                        <div className="
                          flex
                          justify-between
                        ">

                          <span className="
                            text-gray-500
                          ">
                            GST Amount
                          </span>

                          <span className="
                            font-semibold
                            text-orange-600
                          ">
                            ₹
                            {gstAmount.toFixed(2)}
                          </span>

                        </div>

                        {/* NET */}

                        <div className="
                          border-t
                          pt-3
                          flex
                          justify-between
                          items-center
                          gap-3
                        ">

                          <span className="
                            text-lg
                            font-bold
                          ">
                            Net Total
                          </span>

                          <span className="
                            text-xl
                            md:text-2xl
                            font-bold
                            text-blue-600
                          ">
                            ₹
                            {netTotal.toFixed(2)}
                          </span>

                        </div>

                        {/* SAVE */}

                        {!isConverted && (

                          <button
                            onClick={
                              saveQuotation
                            }
                            className="
                              w-full
                              bg-green-600
                              hover:bg-green-700
                              text-white
                              px-4
                              py-3
                              rounded-xl
                              font-semibold
                              shadow
                            "
                          >
                            💾 Save Changes
                          </button>

                        )}

                      </div>

                    </div>

                  );

                })()}

              </div>

            </motion.div>

          </motion.div>

        )}

      </AnimatePresence>

      {/* =================================================
          MOBILE FILTER
      ================================================= */}

      <AnimatePresence>

        {showFilter && (

          <>
            <motion.div
              className="
                fixed
                inset-0
                bg-black/50
                z-[9998]
              "
              initial={{
                opacity: 0
              }}
              animate={{
                opacity: 1
              }}
              exit={{
                opacity: 0
              }}
              onClick={() =>
                setShowFilter(false)
              }
            />

            <motion.div
              initial={{
                y: "100%"
              }}
              animate={{
                y: 0
              }}
              exit={{
                y: "100%"
              }}
              className="
                fixed
                bottom-0
                left-0
                w-full
                bg-white
                rounded-t-3xl
                p-5
                z-[9999]
              "
            >

              <h2 className="
                text-lg
                font-bold
                mb-4
              ">
                Filters
              </h2>

              <input
                placeholder="Search customer / phone"
                value={tempSearch}
                onChange={e =>
                  setTempSearch(
                    e.target.value
                  )
                }
                className="
                  border
                  w-full
                  p-3
                  rounded-xl
                  mb-3
                "
              />

              <div className="
                flex
                gap-2
                mb-3
              ">

                {[
                  "",
                  "completed",
                  "pending"
                ].map(status => (

                  <button
                    key={status}
                    onClick={() =>
                      setTempStatus(
                        status
                      )
                    }
                    className={`
                      flex-1
                      py-2
                      rounded-xl
                      text-sm
                      ${
                        tempStatus ===
                        status
                          ? "bg-blue-600 text-white"
                          : "bg-gray-100"
                      }
                    `}
                  >
                    {status === ""
                      ? "All"
                      : status}
                  </button>

                ))}

              </div>

              <div className="
                flex
                gap-2
                mb-3
              ">

                {[
                  "all",
                  "today",
                  "month"
                ].map(date => (

                  <button
                    key={date}
                    onClick={() => {

                      setTempDateFilter(
                        date
                      );

                      setTempFromDate(
                        ""
                      );

                      setTempToDate(
                        ""
                      );

                    }}
                    className={`
                      flex-1
                      py-2
                      rounded-xl
                      text-sm
                      ${
                        tempDateFilter ===
                        date
                          ? "bg-purple-600 text-white"
                          : "bg-gray-100"
                      }
                    `}
                  >
                    {date}
                  </button>

                ))}

              </div>

              <div className="
                grid
                grid-cols-2
                gap-2
                mb-4
              ">

                <input
                  type="date"
                  value={
                    tempFromDate
                  }
                  onChange={e => {

                    setTempFromDate(
                      e.target.value
                    );

                    setTempDateFilter(
                      "all"
                    );

                  }}
                  className="
                    border
                    p-2
                    rounded-xl
                    w-full
                  "
                />

                <input
                  type="date"
                  value={
                    tempToDate
                  }
                  onChange={e => {

                    setTempToDate(
                      e.target.value
                    );

                    setTempDateFilter(
                      "all"
                    );

                  }}
                  className="
                    border
                    p-2
                    rounded-xl
                    w-full
                  "
                />

              </div>

              <button
                onClick={() => {

                  setSearch(
                    tempSearch
                  );

                  setStatusFilter(
                    tempStatus
                  );

                  setDateFilter(
                    tempDateFilter
                  );

                  setFromDate(
                    tempFromDate
                  );

                  setToDate(
                    tempToDate
                  );

                  setCurrentPage(1);

                  setShowFilter(
                    false
                  );

                }}
                className="
                  w-full
                  bg-blue-600
                  text-white
                  py-3
                  rounded-xl
                  font-semibold
                "
              >
                Apply Filter
              </button>

              <button
                onClick={() => {

                  setTempSearch("");
                  setTempStatus("");
                  setTempDateFilter(
                    "all"
                  );
                  setTempFromDate("");
                  setTempToDate("");

                  setSearch("");
                  setStatusFilter("");
                  setDateFilter(
                    "all"
                  );
                  setFromDate("");
                  setToDate("");

                  setCurrentPage(1);

                  setShowFilter(
                    false
                  );

                }}
                className="
                  mt-2
                  w-full
                  py-3
                  bg-gray-100
                  rounded-xl
                "
              >
                Clear All
              </button>

            </motion.div>

          </>

        )}

      </AnimatePresence>

      {/* =================================================
          PAGINATION
      ================================================= */}

      {totalPages > 1 && (

        <div className="
          flex
          justify-center
          items-center
          mt-6
          gap-2
          flex-wrap
        ">

          {getPagination(
            currentPage,
            totalPages
          ).map(
            (page, i) =>

              page === "..." ? (

                <span
                  key={i}
                  className="
                    px-2
                    text-gray-400
                  "
                >
                  ...
                </span>

              ) : (

                <button
                  key={i}
                  onClick={() =>
                    setCurrentPage(
                      page
                    )
                  }
                  className={`
                    px-3
                    py-1
                    border
                    rounded-lg
                    ${
                      currentPage === page
                        ? "bg-blue-600 text-white"
                        : "bg-white"
                    }
                  `}
                >
                  {page}
                </button>

              )

          )}

        </div>

      )}

    </div>

  );

}